import { isNull } from 'lodash';
import { addYears } from 'date-fns';
import {
  createRoadCareDraftRequest,
  updateRoadCareDraftRequest,
  getRoadCareDraftContractByTokenRequest,
  saveCareContractInCacheRequest,
} from '../../api/roadCareApi';
import {
  bindRoadCareContractAndSaveInLocal,
  mapErrorsToContractSteps,
} from '../../helpers/roadCare/roadCareContractHelpers';
import { setSaveCard } from './payment';
import {
  roadCareCalculationReducerForContract,
  SET_MOBILE_CONTRACT_DATA,
  SET_MOBILE_CONTRACT_DATA_FAIL,
  SET_MOBILE_CONTRACT_DATA_SUCCESS,
} from './roadCareCalculation';
import { ekengVehicleDataRequest } from '../../api/vehicleApi';
import { getArmenianStartDate } from '../../helpers/dates';
import { PAYMENT_FREQUENCIES, SINGLE } from '../../constants/paymentFrequency';
import { getTransportUsageTypes } from './vehicles';
import { ACTION_NAME, APPLICATION_CONTRACT_TYPES } from '../../constants';
import { getCityVillages } from './countries';
import { ROAD_CARE_SEARCH_VEHICLE_BACKEND_ERROR_MESSAGES } from '../../constants/roadCare';
import { fetchMobileContractDataFromMobileApiRequest } from '../../api/contractApi';
import { bindOncoCareContractCalculationData } from '../../helpers/oncoCare/bindData';
import { bindMobileContractUserData } from '../../helpers/contractHelpers';

const SET_SIGNER = 'efes/roadCareCareContract/SET_SIGNER';
const SET_HAS_ADD_SIGNER = 'efes/roadCareCareContract/SET_HAS_ADD_SIGNER';
const SET_ROAD_CARE_DETAILS = 'efes/roadCareCareContract/SET_ROAD_CARE_DETAILS';
const SET_PAYMENT_METHOD = 'efes/roadCareCareContract/SET_PAYMENT_METHOD';

const SET_HAS_AGREED = 'efes/roadCareCareContract/SET_HAS_AGREED';
const SET_HAS_AGREED_AUTOMATIC_RECURRING = 'efes/roadCareCareContract/SET_HAS_AGREED_AUTOMATIC_RECURRING';

const SAVE_DRAFT_CONTRACT = 'efes/roadCareCareContract/SAVE_DRAFT_CONTRACT';
const SAVE_DRAFT_CONTRACT_SUCCESS = 'efes/roadCareCareContract/SAVE_DRAFT_CONTRACT_SUCCESS';
const SAVE_DRAFT_CONTRACT_FAIL = 'efes/roadCareCareContract/SAVE_DRAFT_CONTRACT_FAIL';

const SAVE_ROAD_CARE_CONTRACT_IN_CACHE = 'efes/roadCareCareContract/SAVE_ROAD_CARE_CONTRACT_IN_CACHE';
const SAVE_ROAD_CARE_CONTRACT_IN_CACHE_SUCCESS = 'efes/roadCareCareContract/SAVE_ROAD_CARE_CONTRACT_IN_CACHE_SUCCESS';
const SAVE_ROAD_CARE_CONTRACT_IN_CACHE_FAIL = 'efes/roadCareCareContract/SAVE_ROAD_CARE_CONTRACT_IN_CACHE_FAIL';

const GET_DRAFT_ROAD_CARE_CONTRACT = 'efes/roadCareCareContract/GET_DRAFT_ROAD_CARE_CONTRACT';
const GET_DRAFT_ROAD_CARE_CONTRACT_SUCCESS = 'efes/roadCareCareContract/GET_DRAFT_ROAD_CARE_CONTRACT_SUCCESS';
const GET_DRAFT_ROAD_CARE_CONTRACT_FAIL = 'efes/roadCareCareContract/GET_DRAFT_ROAD_CARE_CONTRACT_FAIL';

const CLEAR_ROAD_CARE_CONTRACT = 'efes/roadCareCareContract/CLEAR_ROAD_CARE_CONTRACT';
const CLEAR_SIGNER_ERRORS = 'efes/roadCareCareContract/CLEAR_SIGNER_ERRORS';
const CLEAR_CALCULATOR_ERRORS = 'efes/roadCareCareContract/CLEAR_CALCULATOR_ERRORS';

const SEARCH_EKENG_VEHICLE_DATA = 'efes/roadCareCareContract/SEARCH_EKENG_VEHICLE_DATA';
const SEARCH_EKENG_VEHICLE_DATA_SUCCESS = 'efes/roadCareCareContract/SEARCH_EKENG_VEHICLE_DATA_SUCCESS';
const SEARCH_EKENG_VEHICLE_DATA_ERROR = 'efes/roadCareCareContract/SEARCH_EKENG_VEHICLE_DATA_ERROR';

const SET_SEARCH_EKENG_VEHICLE_FIELDS_DATA = 'efes/roadCareCareContract/SET_SEARCH_EKENG_VEHICLE_FIELDS_DATA';
const SET_RELEASE_YEAR = 'efes/roadCareCareContract/SET_RELEASE_YEAR';

const SET_MOBILE_TOKEN = 'efes/roadCareCareContract/SET_MOBILE_TOKEN';

const initialState = {
  contractData: {
    draftId: '',
    hasAddedSigner: true,
    hasAgreedAutomaticRecurring: false,
    signer: {},
    roadCareDetails: {
      releaseYear: 0,
      transportUsageType: {},
      defaultCoverages: [],
      isFetchingDefaultCoverages: false,
      riskAmount: 0,
      riskAmounts: [],
      isFetchingRiskAmounts: false,
      fetchRiskAmountsError: null,
      isFetchingPrice: false,
      prices: {},
      paymentFrequency: PAYMENT_FREQUENCIES[SINGLE],
      paymentFrequencies: {},
      riskAdverse: 0,
      range: {
        start: getArmenianStartDate(2),
        end: addYears(getArmenianStartDate(1), 1),
      },
    },
    mobileToken: null,
  },
  hasAgreed: false,
  paymentMethod: '',
  loadingDraftContract: false,
  draftToken: null,
  orderId: null,
  savingContractInCache: false,
  signerBackendErrors: null,
  insuredPersonBackendErrors: null,
  calculatorBackendErrors: null,
  ekengVehicleData: {},
  ekengVehicleErrors: null,
  isFetchingEkengVehicleData: false,
  searchVehicleFieldsData: {
    passport: '',
    plateNumber: '',
    hasAgreed: false,
  },
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_SIGNER:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          signer: action.data,
        },
      };
    case SET_HAS_ADD_SIGNER:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          hasAddedSigner: action.data,
        },
      };
    case SET_RELEASE_YEAR:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          roadCareDetails: {
            ...state.contractData.roadCareDetails,
            releaseYear: action.data,
          },
        },
      };

    case SET_PAYMENT_METHOD:
      return {
        ...state,
        paymentMethod: action.data,
      };
    case SET_HAS_AGREED:
      return {
        ...state,
        hasAgreed: action.data,
      };
    case SET_HAS_AGREED_AUTOMATIC_RECURRING:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          hasAgreedAutomaticRecurring: action.data,
        },
      };
    case SAVE_DRAFT_CONTRACT_SUCCESS:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          draftId: action.result,
        },
      };

    case SAVE_ROAD_CARE_CONTRACT_IN_CACHE:
      return {
        ...state,
        draftToken: null,
        orderId: null,
        savingContractInCache: true,
      };
    case SAVE_ROAD_CARE_CONTRACT_IN_CACHE_SUCCESS:
      return {
        ...state,
        draftToken: action.result.draftToken,
        orderId: action.result.id,
        savingContractInCache: false,
      };
    case SAVE_ROAD_CARE_CONTRACT_IN_CACHE_FAIL:
      return {
        ...state,
        draftToken: null,
        orderId: null,
        savingContractInCache: false,
        ...action.backendErrors,
      };
    case SET_ROAD_CARE_DETAILS: {
      return {
        ...state,
        contractData: {
          ...state.contractData,
          roadCareDetails: { ...state.contractData.roadCareDetails, ...action.data },
        },
      };
    }

    case GET_DRAFT_ROAD_CARE_CONTRACT:
      return {
        ...state,
        loadingDraftContract: true,
      };
    case GET_DRAFT_ROAD_CARE_CONTRACT_SUCCESS:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          ...action.result.contractData,
          draftId: action.result.orderId,
          roadCareDetails: {
            ...state.contractData.roadCareDetails,
            ...action.result.contractData.roadCareDetails,
          },
        },
        orderId: action.result.orderId,
        searchVehicleFieldsData: action.result.searchVehicleFieldsData,
        draftToken: action.result.draftToken,

        loadingDraftContract: false,
      };
    case GET_DRAFT_ROAD_CARE_CONTRACT_FAIL:
      return {
        ...state,
        loadingDraftContract: false,
      };

    case CLEAR_SIGNER_ERRORS:
      return {
        ...state,
        signerBackendErrors: null,
      };
    case CLEAR_CALCULATOR_ERRORS:
      return {
        ...state,
        calculatorBackendErrors: null,
      };
    case CLEAR_ROAD_CARE_CONTRACT:
      return {
        ...initialState,
      };

    case SEARCH_EKENG_VEHICLE_DATA:
      return {
        ...state,
        ekengVehicleData: {},
        isFetchingEkengVehicleData: true,
        ekengVehicleErrors: null,
      };
    case SEARCH_EKENG_VEHICLE_DATA_SUCCESS:
      return {
        ...state,
        ekengVehicleData: action.data,
        isFetchingEkengVehicleData: false,
        ekengVehicleErrors: null,
      };
    case SEARCH_EKENG_VEHICLE_DATA_ERROR:
      return {
        ...state,
        ekengVehicleData: {},
        isFetchingEkengVehicleData: false,
        ekengVehicleErrors: action.error,
      };

    case SET_SEARCH_EKENG_VEHICLE_FIELDS_DATA:
      return {
        ...state,
        searchVehicleFieldsData: action.data,
      };
    case SET_MOBILE_TOKEN:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          mobileToken: action.data,
        },
      };
    default:
      return {
        ...state,
        contractData: {
          ...state.contractData,
          roadCareDetails: roadCareCalculationReducerForContract(
            state.contractData.roadCareDetails,
            action,
          ),
        },
      };
  }
}

export const saveDraftContract = () => (dispatch, getStore) => {
  const { contractData } = getStore().roadCareContract;
  const { draftId } = contractData;
  return dispatch({
    actions: [
      { type: SAVE_DRAFT_CONTRACT },
      { type: SAVE_DRAFT_CONTRACT_SUCCESS },
      { type: SAVE_DRAFT_CONTRACT_FAIL },
    ],
    promise: draftId
      ? updateRoadCareDraftRequest(contractData)
      : createRoadCareDraftRequest(contractData),
  });
};

export const setSigner = (data) => ({ type: SET_SIGNER, data });

export const setPaymentMethod = (data) => ({ type: SET_PAYMENT_METHOD, data });

export const setRoadCareDetails = (data) => ({ type: SET_ROAD_CARE_DETAILS, data });

export const setHasAgreed = (data) => ({ type: SET_HAS_AGREED, data });

export const setMobileToken = (data) => ({ type: SET_MOBILE_TOKEN, data });

export const clearRoadCareContract = () => ({ type: CLEAR_ROAD_CARE_CONTRACT });

export const setOrUpdateSigner = (signerData) => (dispatch) => {
  dispatch(setSigner(signerData));
};

export const clearSignerBackendErrors = () => ({ type: CLEAR_SIGNER_ERRORS });

export const clearCalculationBackendErrors = () => ({
  type: CLEAR_CALCULATOR_ERRORS,
});

export const searchEkengVehicle = (data) => (dispatch) => {
  dispatch({ type: SEARCH_EKENG_VEHICLE_DATA });
  return ekengVehicleDataRequest(data)
    .then((response) => {
      dispatch({ type: SEARCH_EKENG_VEHICLE_DATA_SUCCESS, data: response.data });
      dispatch({ type: SET_RELEASE_YEAR, data: response.data.released });
      return response;
    })
    .catch((error) => {
      const showError = !ROAD_CARE_SEARCH_VEHICLE_BACKEND_ERROR_MESSAGES.includes(
        error.response.data.message,
      );
      dispatch({
        type: SEARCH_EKENG_VEHICLE_DATA_ERROR,
        showError,
        customMsg: !!showError,
        error: error.response.data,
      });
    });
};

export const setSearchVehicleFieldsData = (data) => (
  { type: SET_SEARCH_EKENG_VEHICLE_FIELDS_DATA, data }
);

export const saveRoadCareCareContractInCache = () => async (dispatch, getStore) => {
  const { contractData, searchVehicleFieldsData, ekengVehicleData } = getStore().roadCareContract;
  dispatch({ type: SAVE_ROAD_CARE_CONTRACT_IN_CACHE });
  return saveCareContractInCacheRequest({
    ...contractData,
    searchVehicleFieldsData,
    ekengVehicleData,
  })
    .then((response) => {
      dispatch({ type: SAVE_ROAD_CARE_CONTRACT_IN_CACHE_SUCCESS, result: response.data });
      return response.data;
    })
    .catch((error) => {
      const backendErrors = mapErrorsToContractSteps(
        error.response.data.errors,
        contractData,
      );
      dispatch({
        type: SAVE_ROAD_CARE_CONTRACT_IN_CACHE_FAIL,
        showError: !!(
          isNull(backendErrors.signerBackendErrors) && isNull(backendErrors.calculatorBackendErrors)
        ),
        customMsg: 'contactAgent',
        backendErrors,
      });
      return Promise.reject();
    });
};

export const getDraftRoadCareContractAndSaveInCache = (id, token) => (dispatch, getStore) => {
  dispatch({ type: GET_DRAFT_ROAD_CARE_CONTRACT });
  return getRoadCareDraftContractByTokenRequest(id, token).then(async (response) => {
    const { data } = response;
    const { countries, states } = getStore().countriesReducer;
    await dispatch(getCityVillages(data.signer.contactInfo.stateId));
    const { cityVillages } = getStore().countriesReducer;
    const bindData = bindRoadCareContractAndSaveInLocal(data, countries, states, cityVillages);
    dispatch(setMobileToken(data.mobileToken));
    dispatch(setSaveCard(bindData.isSaveCard));
    dispatch({ type: GET_DRAFT_ROAD_CARE_CONTRACT_SUCCESS, result: { ...bindData, orderId: id } });
    await dispatch(searchEkengVehicle({
      techPassport: bindData.searchVehicleFieldsData.passport,
      plateNumber: bindData.searchVehicleFieldsData.plateNumber,
    }));
    await dispatch(getTransportUsageTypes(APPLICATION_CONTRACT_TYPES.ROAD_CARE));
    dispatch(saveRoadCareCareContractInCache());

    return { id: response.id, draftToken: response.draftToken };
  }).catch(() => {
    dispatch({ type: GET_DRAFT_ROAD_CARE_CONTRACT_FAIL, customMsg: 'contactAgent', showError: true });
  });
};

export const getMobileContractData = (token, prefix) => (dispatch) => {
  dispatch({
    actions: [
      { type: SET_MOBILE_CONTRACT_DATA, name: ACTION_NAME.CALCULATOR },
      { type: SET_MOBILE_CONTRACT_DATA_SUCCESS, name: ACTION_NAME.CALCULATOR },
      {
        type: SET_MOBILE_CONTRACT_DATA_FAIL,
        name: ACTION_NAME.CALCULATOR,
        showError: false,
      },
    ],
    promise: fetchMobileContractDataFromMobileApiRequest(`${prefix}/user-info?key=${token}`).then(
      (response) => {
        const userDetails = bindMobileContractUserData(
          { ...response.userDetails, email: response.email },
        );
        dispatch(setOrUpdateSigner({ ...userDetails, resident: 1, memberId: 0 }));
        dispatch(setMobileToken(token));
        return bindOncoCareContractCalculationData(response.calculatorDetails);
      },
    ),
  });
};
